/**
 * MinistrareIA - Chat List Component (Simplified)
 * WhatsApp Web-style list
 * Data comes pre-normalized from backend
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { 
  Search, Users, User, Archive, Check, CheckCheck, 
  Image, Mic, Video, FileText, Clock, Pin, BellOff, ChevronDown 
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import chatUtils from '../../utils/wa-personal/chat_utils';

const ChatList = ({ 
  chats, 
  selectedChat, 
  onSelectChat, 
  loading,
  onLoadMore,
  hasMore = false 
}) => {
  const { colors } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [hoveredChat, setHoveredChat] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && searchTerm) {
        setSearchTerm('');
        searchInputRef.current?.blur();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm]);

  // Filter and search chats (data comes pre-sorted from backend)
  const filteredChats = useMemo(() => {
    let result = chats || [];

    // Apply filters
    if (filter === 'unread') {
      result = result.filter(chat => chat.unreadCount > 0);
    } else if (filter === 'groups') {
      result = result.filter(chat => chat._isGroup || chatUtils.isGroup(chat.remoteJid));
    } else if (filter === 'favorites') {
      result = result.filter(chat => chat.pinned);
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(chat => {
        const name = chatUtils.getChatDisplayName(chat).toLowerCase();
        const phone = (chat._phoneNumber || '').toLowerCase();
        const lastMsg = chatUtils.getMessagePreview(chat).toLowerCase();
        
        return name.includes(term) || phone.includes(term) || lastMsg.includes(term);
      });
    }

    return result;
  }, [chats, filter, searchTerm]);

  // Get message icon
  const getMessageIcon = useCallback((msg) => {
    if (!msg || typeof msg !== 'object') return null;
    const style = { flexShrink: 0, opacity: 0.7 };
    
    if (msg.imageMessage) return <Image size={15} style={style} />;
    if (msg.videoMessage) return <Video size={15} style={style} />;
    if (msg.audioMessage) return <Mic size={15} style={style} />;
    if (msg.documentMessage) return <FileText size={15} style={style} />;
    
    return null;
  }, []);

  // Get message status icon
  const getMessageStatus = useCallback((chat) => {
    const lastMsg = chat.lastMessage;
    if (!lastMsg?.key?.fromMe) return null;
    
    const status = lastMsg.status;
    
    if (status === 'READ' || status === 'read') {
      return <CheckCheck size={16} style={{ color: '#53bdeb', flexShrink: 0 }} />;
    }
    if (status === 'DELIVERY_ACK' || status === 'delivered') {
      return <CheckCheck size={16} style={{ color: colors.textTertiary, flexShrink: 0 }} />;
    }
    if (status === 'SERVER_ACK' || status === 'sent') {
      return <Check size={16} style={{ color: colors.textTertiary, flexShrink: 0 }} />;
    }
  }, [colors]);

  // Check if chat is selected
  const isChatSelected = useCallback((chat) => {
    return selectedChat && chatUtils.isSameChat(chat, selectedChat);
  }, [selectedChat]);

  // Filter options
  const filterOptions = [
    { id: 'all', label: 'Tudo' },
    { id: 'unread', label: 'Não lidas' },
    { id: 'favorites', label: 'Favoritas' },
    { id: 'groups', label: 'Grupos' },
  ];

  // Styles
  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: colors.bgPrimary,
    },
    header: {
      padding: '10px 14px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    searchWrapper: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      padding: '0 12px',
      marginBottom: '10px',
      transition: 'box-shadow 0.2s',
      ...(searchFocused && { boxShadow: `0 0 0 2px ${colors.accentPrimary}40` }),
    },
    searchInput: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      padding: '10px 12px',
      fontSize: '15px',
      color: colors.textPrimary,
      outline: 'none',
    },
    filtersContainer: {
      display: 'flex',
      gap: '6px',
      overflowX: 'auto',
      paddingBottom: '2px',
    },
    filterBtn: {
      padding: '7px 14px',
      border: 'none',
      backgroundColor: colors.bgTertiary,
      color: colors.textSecondary,
      borderRadius: '18px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
    },
    filterBtnActive: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
    },
    archiveRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px 20px',
      cursor: 'pointer',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    listContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    chatItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
      cursor: 'pointer',
      transition: 'background-color 0.1s',
    },
    avatar: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      overflow: 'hidden',
      marginRight: '12px',
      flexShrink: 0,
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    chatInfo: {
      flex: 1,
      minWidth: 0,
      paddingBottom: '10px',
    },
    chatHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
    },
    chatName: {
      fontWeight: 500,
      fontSize: '17px',
      color: colors.textPrimary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '70%',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    chatTime: {
      fontSize: '12px',
      color: colors.textTertiary,
      flexShrink: 0,
    },
    chatPreview: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    chatPreviewText: {
      flex: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    chatBadge: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: '11px',
      fontWeight: 600,
      minWidth: '20px',
      height: '20px',
      padding: '0 6px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '8px',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 24px',
      color: colors.textTertiary,
      textAlign: 'center',
    },
    skeleton: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
    },
    skeletonAvatar: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: colors.bgTertiary,
      marginRight: '12px',
      animation: 'pulse 1.5s infinite',
    },
    skeletonLine: {
      height: '14px',
      borderRadius: '4px',
      backgroundColor: colors.bgTertiary,
      marginBottom: '8px',
      animation: 'pulse 1.5s infinite',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header with Search and Filters */}
      <div style={styles.header}>
        <div style={styles.searchWrapper}>
          <Search 
            size={18} 
            style={{ color: searchFocused ? colors.accentPrimary : colors.textTertiary }} 
          />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Pesquisar ou começar nova conversa"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={styles.searchInput}
          />
          {searchTerm && (
            <button
              style={{ padding: '4px', border: 'none', background: 'transparent', color: colors.textTertiary, cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        <div style={styles.filtersContainer}>
          {filterOptions.map((f) => (
            <button
              key={f.id}
              style={{
                ...styles.filterBtn,
                ...(filter === f.id ? styles.filterBtnActive : {}),
              }}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results count when searching */}
      {searchTerm && (
        <div style={{ padding: '8px 14px', fontSize: '12px', color: colors.textTertiary, borderBottom: `1px solid ${colors.borderColor}` }}>
          {filteredChats.length} conversa{filteredChats.length !== 1 ? 's' : ''} encontrada{filteredChats.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Chat Items */}
      <div ref={listRef} style={styles.listContainer}>
        {loading ? (
          // Skeleton loading
          [...Array(6)].map((_, i) => (
            <div key={i} style={styles.skeleton}>
              <div style={styles.skeletonAvatar} />
              <div style={{ flex: 1 }}>
                <div style={{ ...styles.skeletonLine, width: '65%' }} />
                <div style={{ ...styles.skeletonLine, width: '45%', height: '12px' }} />
              </div>
            </div>
          ))
        ) : filteredChats.length === 0 ? (
          // Empty state
          <div style={styles.emptyState}>
            <div style={{ width: 80, height: 80, backgroundColor: colors.bgTertiary, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Search size={32} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 500, color: colors.textPrimary, marginBottom: 8 }}>
              {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa'}
            </p>
            <p style={{ fontSize: 14 }}>
              {searchTerm ? 'Tente buscar por outro nome ou número' : 'Suas conversas aparecerão aqui'}
            </p>
          </div>
        ) : (
          // Chat list
          filteredChats.map((chat) => {
            const isSelected = isChatSelected(chat);
            const isHovered = hoveredChat === chat.remoteJid;
            const displayName = chatUtils.getChatDisplayName(chat);
            const preview = chatUtils.getMessagePreview(chat);
            const hasUnread = chat.unreadCount > 0;
            const isGroupChat = chat._isGroup || chatUtils.isGroup(chat.remoteJid);

            return (
              <div
                key={chat.id || chat.remoteJid}
                style={{
                  ...styles.chatItem,
                  backgroundColor: isSelected ? colors.bgTertiary : isHovered ? colors.bgSecondary : 'transparent',
                }}
                onClick={() => onSelectChat(chat)}
                onMouseEnter={() => setHoveredChat(chat.remoteJid)}
                onMouseLeave={() => setHoveredChat(null)}
              >
                {/* Avatar */}
                <div style={styles.avatar}>
                  {chat.profilePicUrl ? (
                    <img 
                      src={chat.profilePicUrl} 
                      alt="" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={styles.avatarPlaceholder}>
                      {isGroupChat ? <Users size={24} /> : <User size={24} />}
                    </div>
                  )}
                </div>
                
                {/* Chat Info */}
                <div style={styles.chatInfo}>
                  <div style={styles.chatHeader}>
                    <span style={styles.chatName}>
                      {displayName}
                      {chat.pinned && <Pin size={14} style={{ color: colors.textTertiary }} />}
                    </span>
                    <span style={{ ...styles.chatTime, ...(hasUnread && { color: colors.accentPrimary, fontWeight: 500 }) }}>
                      {chatUtils.formatChatTime(chat.updatedAt)}
                    </span>
                  </div>
                  
                  <div style={styles.chatPreview}>
                    {chat.lastMessage?.key?.fromMe && getMessageStatus(chat)}
                    {getMessageIcon(chat.lastMessage?.message)}
                    <span style={styles.chatPreviewText}>{preview || 'Clique para ver a conversa'}</span>
                    
                    {chat.muted && <BellOff size={14} style={{ color: colors.textTertiary, marginLeft: 8 }} />}
                    {hasUnread && (
                      <div style={styles.chatBadge}>
                        {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        
        {/* Load more button */}
        {hasMore && (
          <div 
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, color: colors.accentPrimary, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
            onClick={onLoadMore}
          >
            <ChevronDown size={18} />
            Carregar mais conversas
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        input::placeholder { color: ${colors.textTertiary}; }
      `}</style>
    </div>
  );
};

export default ChatList;